import { Button, Input, InputSelect, Label, Checkbox } from '@src/features/ui';
import StepProgress from '@src/features/user/checkout/layout/StepProgress';
import { PaymentFormData, useCheckoutStore } from '@src/stores/checkoutStore';
import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchAddOnsData } from '@src/services/products';
import { AddonType, OnboardingReq, TradingPlatForms } from '@src/types';
import { AcceptedTerm } from '@src/types/termsAndConditions';
import { AddonPerformanceAccountFee } from '@constants/addons';
import { saveOnboarding, validateCoupon } from '@src/services';
import { toast } from 'react-toastify';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useCountryOptions } from '@hooks/useCountryOptions.ts';
import { useStateOptions } from '@hooks/useStateOptions.ts';
import { useTranslation } from 'react-i18next';
import { useGetMe } from '@src/hooks/useGetMe';
import { CheckIcon } from 'lucide-react';
import { dateSelectData } from '@features/admin/productsAddons/productsAddonsData.ts';
import { ReBillTimesType, PeriodUnit } from '@src/types/productsAddons';

import { accountTypeData, iconsImgPackage } from './data';

const MONTHLY_EXCLUDED_ADDON_TYPES = new Set([
  'performance_account_fee',
  'eval_discount_x_reset',
]);

const schema = yup.object().shape({
  coupon_code: yup.string(),
  first_name: yup.string().required('First name is required'),
  last_name: yup.string().required('Last name is required'),
  referral_source: yup.string(),
  payment_type: yup.string().required('Payment type is required'),
  street: yup.string().required('Street is required'),
  city: yup.string().required('City is required'),
  country: yup.string().required('Country is required'),
  state: yup.string().when('country', {
    is: 'US',
    then: (schema) => schema.required('State is required'),
  }),
  zip_code: yup.string().when('country', {
    is: 'US',
    then: (schema) => schema.required('Zip code is required'),
  }),
  phone_number: yup.string().required('Phone number is required'),
});

interface AddOnItem {
  id: number;
  name: string;
  price: number;
  description: string;
  addon_type: string;
  title: string;
  billing_plan: {
    first_price: number;
    addon_monthly_price?: number;
    second_price?: number;
  };
  display_name_web: string;
  addon_additional_drawdown_option?: number | null;
  addon_performance_account_fee_option?: AddonPerformanceAccountFee;
}

// UI group item shape including IDs so we can map selections to backend IDs
type UiAddOnItem = {
  uid: string; // stable identifier matching checkoutStore key convention
  addonId?: number; // real backend addon ID (only for API items)
  id?: number; // legacy — same as addonId; kept for compat
  title?: string;
  sub?: string;
  price?: number;
  addonMonthlyPrice?: number;
  rangular?: number;
  first_period_unit?: string;
  priceNote?: string;
  addon_performance_account_fee_option?: AddonPerformanceAccountFee;
};

type MappedAddOnData = {
  title: string;
  title2?: string;
  description?: string;
  optional?: boolean;
  data: UiAddOnItem[];
  data2?: UiAddOnItem[];
  note?: string;
  addon_type: string;
};

const titles: Record<
  string,
  {
    title: string;
    note?: string;
    desc?: string;
  }
> = {
  eval_quality_x_days: {
    title: 'Min days to qualify after reaching profit target ',
    note: '<b>Note</b>: Payout requests may be submitted on the nearest Saturday between <b>2:00 AM and 5:00 PM EST.</b>',
    desc: 'This is a one-time charge',
  },
  eval_discount_x_reset: {
    title: 'Practice Resets',
    note: '<b>Note</b>: A reset does not change the subscription renewal date (monthly plan) or the <b>30-day expiry</b> (non-recurring plan).',
    desc: 'Save by buying now',
  },
  pa_payout_x_days: {
    title: 'Payout Speed',
    note: '<b>Note</b>: Payout requests may be submitted on the nearest Saturday between <b>2:00 AM and 5:00 PM EST.</b>',
    desc: 'This is a one-time charge',
  },
  performance_account_fee: { title: 'Performance Account Fee' },
  account_bundle: { title: 'Want to make it into a bundle?' },
  drawdown: { title: 'Drawdown' },
};

export const OrderSummary = () => {
  const navigate = useNavigate();
  const {
    platform,
    packages,
    sizeIndex,
    typeIndex,
    addOns,
    payment,
    isPurchaseMode,
    setPlatform,
    isCompetitionAccount,
    paymentFormData,
    setPaymentFormData,
  } = useCheckoutStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidCoupon, setIsValidCoupon] = useState<boolean | null>(null);

  const [addOnsData, setAddOnsData] = useState<MappedAddOnData[]>([]);

  const { t } = useTranslation();
  const { countryOptions } = useCountryOptions();
  const { stateOptions } = useStateOptions();
  const { me } = useGetMe();

  useEffect(() => {
    // If in purchase mode and platform is not set, get it from me.platform
    if (isPurchaseMode && !platform && me?.platform) {
      setPlatform(me.platform);
    }
  }, [isPurchaseMode, platform, me, setPlatform]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    resetField,
    getValues,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      coupon_code: paymentFormData.coupon_code || '',
      first_name: paymentFormData.first_name || '',
      last_name: paymentFormData.last_name || '',
      referral_source: paymentFormData.referral_source || '',
      payment_type: paymentFormData.payment_type || 'stripe',
      street: paymentFormData.street || '',
      city: paymentFormData.city || '',
      country: paymentFormData.country || 'US',
      state: paymentFormData.state || '',
      zip_code: paymentFormData.zip_code || '',
      phone_number: paymentFormData.phone_number || '',
    },
  });

  const country = useWatch({ control, name: 'country' });

  const handleBlurCouponCode = useCallback(async (couponCode: string) => {
    if (couponCode) {
      try {
        const res = await validateCoupon(couponCode);

        if (res.data?.is_valid) {
          setIsValidCoupon(true);
        } else {
          setIsValidCoupon(false);
          toast.error('Invalid coupon code. Please check and try again.');
        }
      } catch (error) {
        setIsValidCoupon(false);
        toast.error('Invalid coupon code. Please check and try again.');
      }
    } else {
      setIsValidCoupon(null);
    }
  }, []);

  const renderCheckValidCoupon = useMemo(() => {
    if (isValidCoupon === true) {
      return <CheckIcon className={'size-4'} color={'#14CA74'} />;
    }

    return null;
  }, [isValidCoupon]);

  const getAddOnsData = useCallback(async () => {
    try {
      const { data } = await fetchAddOnsData(platform);

      const mappedData = (data as AddOnItem[]).reduce<MappedAddOnData[]>(
        (prev, item) => {
          if (
            [
              'eval_quality_x_days',
              'eval_discount_x_reset',
              'pa_payout_x_days',
              'performance_account_fee',
              'account_bundle',
              'drawdown',
              'pa_change_30pct_rule',
            ].includes(item.addon_type)
          ) {
            const existing = prev.find((e) => e.addon_type === item.addon_type);

            if (existing) {
              if (item.addon_additional_drawdown_option) {
                existing.data2 = [...(existing?.data2 || [])].filter(Boolean);
                existing.title2 =
                  item.addon_type === AddonType.Drawdown
                    ? 'Add to your current drawdown additional drawdown (optional; cumulative; max +$1,500)'
                    : existing.title2;
                existing.data2.push({
                  uid: `api2_${item.id}`,
                  addonId: item.id,
                  id: item.id,
                  price: item.billing_plan.first_price,
                  addonMonthlyPrice:
                    item.billing_plan.addon_monthly_price ?? undefined,
                  title: item.display_name_web,
                  sub: item.description,
                  addon_performance_account_fee_option:
                    item.addon_performance_account_fee_option,
                });

                return prev;
              } else {
                existing.data.push({
                  uid: `api_${item.id}`,
                  addonId: item.id,
                  id: item.id,
                  price: item.billing_plan.first_price,
                  addonMonthlyPrice:
                    item.billing_plan.addon_monthly_price ?? undefined,
                  title: item.display_name_web,
                  sub: item.description,
                  addon_performance_account_fee_option:
                    item.addon_performance_account_fee_option,
                });
              }
            } else {
              prev.push({
                note: titles[item.addon_type].note || '',
                optional: item.addon_type === 'eval_discount_x_reset',
                title: titles[item.addon_type].title || '',
                title2:
                  item.addon_type === AddonType.Drawdown &&
                  !!item.addon_additional_drawdown_option
                    ? 'Add to your current drawdown additional drawdown (optional; cumulative; max +$1,500)'
                    : undefined,
                data: !item.addon_additional_drawdown_option
                  ? [
                      {
                        uid: `api_${item.id}`,
                        addonId: item.id,
                        id: item.id,
                        price: item.billing_plan.first_price,
                        addonMonthlyPrice:
                          item.billing_plan.addon_monthly_price ?? undefined,
                        title: item.display_name_web,
                        sub: item.description,
                        addon_performance_account_fee_option:
                          item.addon_performance_account_fee_option,
                      },
                    ]
                  : [],
                data2: item.addon_additional_drawdown_option
                  ? [
                      {
                        uid: `api2_${item.id}`,
                        addonId: item.id,
                        id: item.id,
                        price: item.billing_plan.first_price,
                        addonMonthlyPrice:
                          item.billing_plan.addon_monthly_price ?? undefined,
                        addon_performance_account_fee_option:
                          item.addon_performance_account_fee_option,
                      },
                    ]
                  : undefined,
                description: item.description,
                addon_type: item.addon_type,
              });
            }

            return prev;
          }

          return prev;
        },
        [
          {
            optional: false,
            title: 'Payment & Activation Handling',
            addon_type: 'default',
            data: [
              {
                uid: 'default__s_0',
                title: 'Monthly subscription',
                sub: 'Renews every 30 calendar days ',
                priceNote: 'Auto-reset at renewal',
                price: 5,
                first_period_unit: 'month',
              },
              {
                uid: 'default__s_1',
                title: 'Non-recurring (no subscription)',
                sub: 'Single charge; expires after 30 calendar days',
                priceNote: 'No auto-reset',
                price: 0,
              },
            ],
          },
          {
            optional: false,
            title: 'Want to make it into a bundle?',
            addon_type: 'account_bundle',
            data: [
              {
                uid: 'account_bundle__s_0',
                title: 'No, thanks! Keep only 1 account',
                sub: 'Renews every 30 calendar days ',
                priceNote: 'Auto-reset at renewal',
                price: 0,
              },
            ],
          },
          {
            optional: false,
            title: 'Drawdown',
            addon_type: 'drawdown',
            data: [
              {
                uid: 'drawdown__s_0',
                title: 'Trailing Drawdown',
                price: 0,
              },
            ],
            data2: [
              {
                uid: 'drawdown__s2_0',
                title: '$0 part of base price',
                price: 0,
              },
            ],
          },
          {
            optional: false,
            title: 'Payout Speed',
            addon_type: 'pa_payout_x_days',
            data: [
              {
                uid: 'pa_payout_x_days__s_0',
                title: 'Last Saturday monthly —(No price change)',
                price: 0,
              },
            ],
          },
          {
            optional: false,
            title: 'Performance Account Fee',
            addon_type: 'performance_account_fee',
            data: [],
          },
          {
            optional: true,
            title: 'Practice Resets',
            addon_type: 'eval_discount_x_reset',
            data: [
              {
                uid: 'eval_discount_x_reset__s_0',
                title: 'No Thanks',
                price: 0,
              },
            ],
          },
          {
            optional: false,
            title: 'Min days to qualify after reaching profit target ',
            addon_type: 'eval_quality_x_days',
            data: [
              {
                uid: 'eval_quality_x_days__s_0',
                title: 'No Standard 10 Days (No price change)',
                sub: '',
                price: 0,
              },
            ],
          },
          {
            optional: false,
            title: '30% Rule',
            addon_type: 'pa_change_30pct_rule',
            data: [
              {
                uid: 'pa_change_30pct_rule__s_0',
                title: 'No, thanks! Keep it standard',
                sub: '',
                price: 0,
              },
            ],
          },
        ] as MappedAddOnData[]
      );

      setAddOnsData([...mappedData]);
    } catch (error) {
      console.error('Error fetching add-ons data:', error);
    }
  }, [platform]);

  useEffect(() => {
    if (!platform) navigate('/select-trading-platform');
    else getAddOnsData();
  }, [navigate, platform, getAddOnsData]);

  const isSubscription = useMemo(() => {
    return addOns?.['default'] === 'default__s_0';
  }, [addOns]);

  const paymentData = [
    {
      value: 'stripe',
      label: 'Stripe',
      url: '/assets/stripe.png',
      width: 60,
      height: 24,
    },
    {
      value: 'paypal',
      label: 'PayPal',
      url: '/assets/paypal.png',
      width: 60,
      height: 24,
      disabled: isSubscription,
    },
  ];

  // const getPayoutSpeedOptionsTitle = useCallback(() => {
  //   if (addOns.payoutSpeedOptions === undefined) {
  //     return { title: '', price: 0 };
  //   }
  //   if (addOns.payoutSpeedOptions === 0) {
  //     return { title: 'Standard Payout', price: 0 };
  //   }
  //   if (addOns.payoutSpeedOptions === 1) {
  //     return { title: 'Express Payout', price: 29.99 };
  //   }

  //   return { title: 'Instant Payout', price: 49.99 };
  // }, [addOns.payoutSpeedOptions]);

  // const getOtherCompatible = useCallback(() => {
  //   if (addOns.otherCompatible === undefined) {
  //     return { title: '', price: 0 };
  //   }
  //   if (addOns.otherCompatible === 0) {
  //     return { title: 'Reset discount', price: 39.99 };
  //   }
  //   if (addOns.otherCompatible === 1) {
  //     return { title: 'No min days to qualify', price: 19.99 };
  //   }

  //   return { title: 'Extra $500 First Payout', price: 24.99 };
  // }, [addOns.otherCompatible]);

  const handleConfirm = useCallback(
    async (data: any) => {
      if (!platform) return;
      try {
        setIsSubmitting(true);

        // Map uid selections to real backend addon IDs using addOnsData
        const selectedAddonIds: number[] = [];

        addOnsData.forEach((group) => {
          const sel = addOns[group.addon_type];

          if (!sel) return;

          const pushIdFromUid = (uid: string) => {
            const item =
              group.data.find((d) => d.uid === uid) ??
              group.data2?.find((d) => d.uid === uid);
            const id = item?.addonId ?? item?.id;

            if (id) selectedAddonIds.push(id);
          };

          if (Array.isArray(sel)) sel.forEach(pushIdFromUid);
          else pushIdFromUid(sel);
        });

        const selectedPackageId = selectedPackage?.id;

        if (!selectedPackageId) {
          toast.error('Please select an account size before continuing.');
          setIsSubmitting(false);

          return;
        }

        const billingInfo = {
          first_name: data.first_name,
          last_name: data.last_name,
          street: data.street,
          city: data.city,
          country: data.country,
          state: data.state,
          zip_code: data.zip_code,
          phone_number: data.phone_number,
        };
        const trading_platform =
          (platform.replace(/-\d+$/, '') as TradingPlatForms) || undefined;
        const account_type = (() => {
          if (typeIndex === 2) return 'straight_to_pa';
          if (typeIndex === 1) return 'practice_account';
          if (typeIndex === 3) return 'specialty_account';

          return 'practice_account';
        })();

        // Accept terms from payment.terms_accepted or []
        const acceptedTerms: AcceptedTerm[] = Array.isArray(
          payment?.terms_accepted
        )
          ? (payment.terms_accepted as AcceptedTerm[])
          : [];

        const onboardingRequest: OnboardingReq = {
          trading_platform,
          account_type,
          addon_payment_handling_type:
            addOns['default'] === 'default__s_0' ? 'monthly' : 'non_recurring',
          payment_mode: 'subscription',
          package_id: selectedPackageId,
          addons: selectedAddonIds,
          payment_gateway: data.payment_type,
          coupon_code: data.coupon_code,
          additional_charges: [trading_platform, account_type],
          terms_accepted: acceptedTerms,
          referral_source: data.referral_source,
          billing_info: billingInfo,
          checkout_info: {
            package_id: selectedPackageId,
            addons: selectedAddonIds,
            payment_type: data.payment_type,
            coupon_code: data.coupon_code,
            billing_info: billingInfo,
          },
        };

        const res = await saveOnboarding(onboardingRequest);

        if (!res.data?.url) {
          navigate('/', { replace: true });

          return;
        }
        window.location.href = res.data.url;
      } catch (error: any) {
        toast.error(
          error?.response?.data?.detail ||
            'Something went wrong. Please try again later.'
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [addOns, addOnsData, navigate, platform, sizeIndex, typeIndex, payment]
  );

  // const total = useMemo(() => {
  //   let _total = 0;

  //   Object.keys(addOns).forEach((e) => {
  //     if (!Array.isArray(addOns[Number(e)])) {
  //       if (addOns.weeklyAddOns) _total += TAddOnsOther.weeklyAddOns;

  //       return;
  //     }
  //     addOns[Number(e)].map((ee) => {
  //       let price = addOnsData[Number(e)].data[ee].price;

  //       if (addOnsData[Number(e)].data[ee].rangular) {
  //         price = addOnsData[Number(e)].data[ee].rangular;
  //       }
  //       if (!price) price = 0;
  //       _total += price;
  //     });
  //   });

  //   _total += getOtherCompatible().price;
  //   _total += getPayoutSpeedOptionsTitle().price;

  //   return _total;
  // }, [addOns, getOtherCompatible, getPayoutSpeedOptionsTitle]);

  const accountTypePrice = useMemo(() => {
    return accountTypeData[(typeIndex || 1) - 1].price;
  }, [typeIndex]);

  const selectedPackage = useMemo(() => {
    return packages?.[sizeIndex || 0];
  }, [sizeIndex, packages]);

  const accountSizePrice = useMemo(() => {
    return selectedPackage?.billing_plan?.first_price || 0;
  }, [selectedPackage]);

  const getTodayCharge = useCallback((item?: UiAddOnItem) => {
    if (!item) return 0;

    // Performance account fee options are charged after qualification, not today.
    if (
      item.addon_performance_account_fee_option ===
        AddonPerformanceAccountFee.MONTHLY ||
      item.addon_performance_account_fee_option ===
        AddonPerformanceAccountFee.LIFETIME_ONE_TIME
    ) {
      return 0;
    }

    return item.rangular ?? item.price ?? 0;
  }, []);

  const findItemByUid = useCallback(
    (group: MappedAddOnData, uid: string): UiAddOnItem | undefined =>
      group.data.find((d) => d.uid === uid) ??
      group.data2?.find((d) => d.uid === uid),
    []
  );

  const addOnsTotal = useMemo(() => {
    let _total = 0;

    addOnsData.forEach((group) => {
      const sel = addOns[group.addon_type];

      if (!sel) return;
      const processUid = (uid: string) => {
        const item = findItemByUid(group, uid);

        _total += getTodayCharge(item);
      };

      if (Array.isArray(sel)) sel.forEach(processUid);
      else processUid(sel);
    });

    return _total;
  }, [addOns, addOnsData, findItemByUid, getTodayCharge]);

  const total = useMemo(() => {
    return addOnsTotal;
  }, [addOnsTotal]);

  const totalDueToday = useMemo(() => {
    if (
      platform === TradingPlatForms.RITHMIC_DATA_FEED &&
      isCompetitionAccount
    ) {
      return total + 35 + accountSizePrice + accountTypePrice;
    }

    return total + 20 + accountSizePrice + accountTypePrice;
  }, [
    accountSizePrice,
    accountTypePrice,
    platform,
    isCompetitionAccount,
    total,
  ]);

  const totalMonthly = useMemo(() => {
    if (!isSubscription) return 0;

    // Payment & Activation Handling (monthly option)
    const defaultGroup = addOnsData.find((g) => g.addon_type === 'default');
    const selectedDefault = addOns['default'];

    if (!defaultGroup || !selectedDefault || Array.isArray(selectedDefault))
      return 0;

    const defaultItem = findItemByUid(defaultGroup, selectedDefault);
    const paymentHandlingMonthly = defaultItem?.price ?? 0;

    // Sum addon monthly prices for selected add-ons (exclude payment handling group)
    let addonsMonthly = 0;

    addOnsData.forEach((group) => {
      if (group.addon_type === 'default') return;
      if (MONTHLY_EXCLUDED_ADDON_TYPES.has(group.addon_type)) return;

      const selected = addOns[group.addon_type];

      if (!selected) return;

      const processUid = (uid: string) => {
        const item = findItemByUid(group, uid);

        if (!item) return;
        addonsMonthly += item.addonMonthlyPrice ?? 0;
      };

      if (Array.isArray(selected)) selected.forEach(processUid);
      else processUid(selected);
    });

    return addonsMonthly + paymentHandlingMonthly;
  }, [addOns, addOnsData, findItemByUid, isSubscription]);

  const productSecondPrice = useMemo(() => {
    return selectedPackage?.billing_plan?.second_price ?? 0;
  }, [selectedPackage]);

  const totalMonthlyWithProduct = useMemo(() => {
    return totalMonthly + productSecondPrice;
  }, [productSecondPrice, totalMonthly]);

  const handlePrevious = () => {
    const purchaseFromDate = getValues();

    setPaymentFormData(purchaseFromDate as Partial<PaymentFormData>);
    navigate('/add-ons');
  };

  if (!platform) return null;

  return (
    <form onSubmit={handleSubmit(handleConfirm)}>
      <div className="min-h-screen pb-[120px]">
        <div
          className="max-w-[1440px] m-auto px-6 lg:px-[60px] mt-4 py-8 flex flex-row justify-between gap-x-10"
          style={{
            backgroundColor:
              'rgba(255, 255, 255, 0.05)' /* 5% = 0.05 opacity */,
            backdropFilter: 'blur(200px)',
          }}
        >
          <div className="flex-1">
            <div className="mb-8">
              <StepProgress
                isAddons
                accountType={typeIndex}
                current={isPurchaseMode ? 4 : 5}
                platform={platform}
                selectedPackage={selectedPackage}
                skipPlatform={isPurchaseMode}
                total={isPurchaseMode ? 4 : 5}
                onClick={(step) => {
                  if (!isPurchaseMode && !step)
                    navigate('/select-trading-platform');
                  if (step === (isPurchaseMode ? 0 : 1))
                    navigate('/select-trading-account-type');
                  if (step === (isPurchaseMode ? 1 : 2))
                    navigate('/select-account');
                  if (step === (isPurchaseMode ? 2 : 3)) navigate('/add-ons');
                }}
              />
            </div>
            <h2 className="font-bold text-[28px] leading-[100%] tracking-normal text-white mb-2">
              Order Summary
            </h2>
            <p className="pt-[10px] font-medium text-[16px] leading-[18px] tracking-normal text-white mb-[36px] max-w-[617px]">
              Review and confirm your order
            </p>
            <div className="flex flex-col gap-[15px]">
              <div className="bg-[#1F2734] rounded-[10px] p-[20px] flex flex-col gap-[30px]">
                <div className="flex flex-col items-start justify-center p-[30px] pt-5 bg-[#253348] rounded-[10px] border-[#33455F] border">
                  <p className="text-[18px] font-bold">Your Selections</p>
                  <div className="flex items-center justify-between w-full gap-[15px]">
                    <div className={'space-y-[10px]'}>
                      <p
                        className={
                          'text-sm text-[#818FA4] font-normal mt-[30px]'
                        }
                      >
                        Trading Platform
                      </p>
                      <div className={'flex items-center gap-[6px] ps-[10px]'}>
                        {
                          iconsImgPackage[
                            platform as keyof typeof iconsImgPackage
                          ].icon
                        }
                        <span className="font-semibold text-[14px] text-white">
                          {
                            iconsImgPackage[
                              platform as keyof typeof iconsImgPackage
                            ].text
                          }
                        </span>
                      </div>
                    </div>
                    <p className={'text-white font-bold text-base'}>$20.00</p>
                  </div>
                  <div className="flex items-center justify-between w-full gap-[15px]">
                    <div className={'space-y-[10px]'}>
                      <p
                        className={
                          'text-sm text-[#818FA4] font-normal mt-[30px]'
                        }
                      >
                        Account Type
                      </p>
                      <div className={'flex items-center gap-[6px] ps-[10px]'}>
                        <div className={'text-white font-semibold text-[14px]'}>
                          {accountTypeData[(typeIndex || 1) - 1].title || ''}
                        </div>
                      </div>
                    </div>
                    <p className={'text-white font-bold text-base'}>
                      ${accountTypePrice.toFixed(2)}
                    </p>
                  </div>

                  <div className="flex items-center justify-between w-full gap-[15px]">
                    <div className={'space-y-[10px]'}>
                      <p
                        className={
                          'text-sm text-[#818FA4] font-normal mt-[30px]'
                        }
                      >
                        Account Size
                      </p>
                      <div className={'flex items-center gap-[6px] ps-[10px]'}>
                        <div className={'text-white font-semibold text-[14px]'}>
                          {selectedPackage?.display_name_app || ''} Account
                        </div>
                      </div>
                    </div>
                    <p className={'text-white font-bold text-base'}>
                      ${accountSizePrice.toFixed(2)}
                    </p>
                  </div>

                  {selectedPackage?.billing_plan &&
                    (() => {
                      const billingTerm = selectedPackage.billing_plan;
                      const unitName = dateSelectData.find(
                        (d) => d.value === billingTerm.first_period_unit
                      )?.label;
                      const unitName2 = dateSelectData.find(
                        (d) => d.value === billingTerm.second_period_unit
                      )?.label;
                      const secondBillingTerm =
                        billingTerm.rebill_times_type !==
                        ReBillTimesType.NoMoreCharges
                          ? `, then $${billingTerm.second_price ?? 0} for each ${billingTerm.second_period_value ?? 0} ${unitName2 ?? ''}`
                          : '';
                      const termText =
                        billingTerm.first_period_unit === PeriodUnit.LIFETIMES
                          ? `$${billingTerm.first_price ?? 0}`
                          : `$${billingTerm.first_price ?? 0} for each ${billingTerm.first_period_value ?? 0} ${unitName ?? ''} ${secondBillingTerm}`;

                      return (
                        <div className={'ps-[10px] mt-1'}>
                          <p className={'text-[#CECECE] text-[12px]'}>
                            {termText}
                          </p>
                        </div>
                      );
                    })()}

                  {platform === TradingPlatForms.RITHMIC_DATA_FEED &&
                    isCompetitionAccount && (
                      <div className="flex items-center justify-between w-full gap-[15px]">
                        <div className={'space-y-[10px]'}>
                          <p
                            className={
                              'text-sm text-[#818FA4] font-normal mt-[30px]'
                            }
                          >
                            Competition Account
                          </p>
                        </div>
                        <p className={'text-white font-bold text-base'}>
                          $15.00
                        </p>
                      </div>
                    )}

                  <div className="flex items-center justify-between w-full gap-[15px]">
                    <div className={'space-y-[10px] w-full'}>
                      <p
                        className={
                          'text-sm text-[#818FA4] font-normal mt-[30px]'
                        }
                      >
                        Add Ons
                      </p>
                      {addOnsData.map((group) => {
                        const sel = addOns[group.addon_type];

                        if (!sel) return null;

                        const items: {
                          title: string;
                          price: number;
                          suffix: string;
                        }[] = [];
                        const pushUid = (uid: string) => {
                          const item = findItemByUid(group, uid);

                          if (!item) return;
                          const title = item.title || group.title;

                          const isMonthlyAddonPrice =
                            isSubscription &&
                            !MONTHLY_EXCLUDED_ADDON_TYPES.has(group.addon_type);

                          const price = isMonthlyAddonPrice
                            ? (item.addonMonthlyPrice ?? 0)
                            : getTodayCharge(item);

                          const suffix = isMonthlyAddonPrice ? '/month' : '';

                          if (title) items.push({ title, price, suffix });
                        };

                        if (Array.isArray(sel)) sel.forEach(pushUid);
                        else pushUid(sel);

                        if (!items.length) return null;

                        return (
                          <div key={group.addon_type} className="mt-2">
                            <p className="text-white font-semibold text-[13px] text-[#818FA4]">
                              {group.title}
                            </p>
                            {items.map((item, idx) => (
                              <div
                                key={idx}
                                className="flex items-center justify-between w-full gap-[6px] ps-[16px]"
                              >
                                <p className="text-white font-normal text-[13px]">
                                  - {item.title}
                                </p>
                                <p className="text-[#818FA4] font-normal text-sm">
                                  ${item.price.toFixed(2)}
                                  {item.suffix}
                                </p>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                      <div
                        className={
                          'flex items-center justify-between w-full gap-[6px] ps-[10px]'
                        }
                      >
                        <p className={'text-white font-bold text-[14px]'}>
                          Total Next Monthly Billing
                        </p>
                        <p
                          className={
                            'text-[#818FA4] font-normal text-[16px] text-bold text-[#FFFFFF]'
                          }
                        >
                          ${totalMonthly.toFixed(2)}
                        </p>
                      </div>
                      <div
                        className={
                          'flex items-center justify-between w-full gap-[6px] ps-[10px]'
                        }
                      >
                        <p className={'text-white font-bold text-[14px]'}>
                          Total
                        </p>
                        <p
                          className={
                            'text-[#818FA4] font-normal text-[16px] text-bold text-[#FFFFFF]'
                          }
                        >
                          ${total.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div
                    className={
                      'border-b-[3px] w-full border-[#33455F] pt-[30px] mb-[30px]'
                    }
                  />

                  <div
                    className={
                      'flex items-center justify-between w-full  gap-[6px]'
                    }
                  >
                    <p className={'text-white font-bold text-[14px]'}>
                      Total (Monthly)
                    </p>
                    <p className={'text-white font-normal text-sm'}>
                      ${totalMonthlyWithProduct.toFixed(2)}
                    </p>
                  </div>

                  <div
                    className={
                      'flex items-center justify-between w-full mt-5  gap-[6px]'
                    }
                  >
                    <p className={'text-white font-bold text-[22px]'}>
                      Total Due Today
                    </p>
                    <p className={'text-white font-normal text-[22px]'}>
                      ${totalDueToday.toFixed(2)}
                    </p>
                  </div>
                </div>
                <div className="flex flex-1 gap-10">
                  <div className="space-y-4 pl-4 lg:pl-0  flex-1">
                    <div className="space-y-2">
                      <Label className="text-sm" htmlFor="coupon_code">
                        {t('checkout.form.enterCoupon')}
                      </Label>
                      <Controller
                        control={control}
                        name="coupon_code"
                        render={({ field }) => (
                          <div>
                            <Input
                              {...field}
                              endIcon={renderCheckValidCoupon}
                              id="coupon_code"
                              inputSize={'lg'}
                              placeholder={t(
                                'checkout.form.enterCouponPlaceholder'
                              )}
                              textSize={'sm'}
                              onBlur={() => {
                                handleBlurCouponCode(field.value || '');
                              }}
                              onChange={(event) => {
                                field.onChange(event.target.value);
                                setIsValidCoupon(null);
                              }}
                            />
                            {errors.coupon_code && (
                              <p className="mt-1 text-xs text-red-500">
                                {errors.coupon_code.message}
                              </p>
                            )}
                          </div>
                        )}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label
                          className="text-sm after:ml-0.5 after:text-red-500 after:content-['*']"
                          htmlFor="first_name"
                        >
                          {t('checkout.form.firstName')}
                        </Label>
                        <Controller
                          control={control}
                          name="first_name"
                          render={({ field }) => (
                            <div>
                              <Input
                                id="first_name"
                                inputSize={'lg'}
                                placeholder={t('checkout.form.firstName')}
                                textSize={'sm'}
                                {...field}
                              />
                              {errors.first_name && (
                                <p className="mt-1 text-xs text-red-500">
                                  {errors.first_name.message}
                                </p>
                              )}
                            </div>
                          )}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          className="text-sm after:ml-0.5 after:text-red-500 after:content-['*']"
                          htmlFor="last_name"
                        >
                          {t('checkout.form.lastName')}
                        </Label>
                        <Controller
                          control={control}
                          name="last_name"
                          render={({ field }) => (
                            <div>
                              <Input
                                id="last_name"
                                inputSize={'lg'}
                                placeholder={t('checkout.form.lastName')}
                                textSize={'sm'}
                                {...field}
                              />
                              {errors.last_name && (
                                <p className="mt-1 text-xs text-red-500">
                                  {errors.last_name.message}
                                </p>
                              )}
                            </div>
                          )}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm" htmlFor="referral_source">
                        {t('checkout.form.hearAboutUs')}
                      </Label>
                      <Controller
                        control={control}
                        name="referral_source"
                        render={({ field }) => (
                          <div>
                            <Input
                              id="referral_source"
                              inputSize={'lg'}
                              placeholder={t(
                                'checkout.form.hearAboutUsPlaceholder'
                              )}
                              textSize={'sm'}
                              {...field}
                            />
                            {errors.referral_source && (
                              <p className="mt-1 text-xs text-red-500">
                                {errors.referral_source.message}
                              </p>
                            )}
                          </div>
                        )}
                      />
                    </div>
                  </div>
                  <div className="space-y-4 lg:space-y-6 pl-4 pb-8 lg:pb-0 lg:pl-0   flex-1">
                    <div>
                      <h3 className="mb-4 text-[26px] font-medium">
                        {t('checkout.paymentTerm')}
                      </h3>

                      <Controller
                        control={control}
                        name="payment_type"
                        render={({ field }) => (
                          <div className="space-y-4">
                            {paymentData.map((item) => (
                              <div
                                key={item.value}
                                className="flex items-center space-x-2"
                              >
                                <Checkbox
                                  checked={field.value === item.value}
                                  className="w-[22px] h-[22px] border-2 border-white"
                                  disabled={item.disabled}
                                  id={item.value}
                                  onCheckedChange={() =>
                                    field.onChange(item.value)
                                  }
                                />
                                <Label
                                  className="flex items-center gap-2 text-[16px] ml-3"
                                  htmlFor={item.value}
                                >
                                  <div className="flex items-center gap-3">
                                    <img
                                      alt={`${item.label}-payment`}
                                      height={item.height}
                                      src={item.url}
                                      width={item.width}
                                    />
                                    <span>{item.label}</span>
                                  </div>
                                </Label>
                              </div>
                            ))}
                            {errors.payment_type && (
                              <p className="mt-1 text-xs text-red-500">
                                {errors.payment_type.message}
                              </p>
                            )}
                          </div>
                        )}
                      />
                    </div>

                    <div className="mt-11 space-y-5">
                      <div className="space-y-2">
                        <Label
                          className="text-sm after:ml-0.5 after:text-red-500 after:content-['*']"
                          htmlFor="street"
                        >
                          {t('checkout.form.street')}
                        </Label>
                        <Controller
                          control={control}
                          name="street"
                          render={({ field }) => (
                            <div>
                              <Input
                                id="street"
                                inputSize={'lg'}
                                placeholder={t(
                                  'checkout.form.streetPlaceholder'
                                )}
                                textSize={'sm'}
                                {...field}
                              />
                              {errors.street && (
                                <p className="mt-1 text-xs text-red-500">
                                  {errors.street.message}
                                </p>
                              )}
                            </div>
                          )}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          className="text-sm after:ml-0.5 after:text-red-500 after:content-['*']"
                          htmlFor="city"
                        >
                          {t('checkout.form.city')}
                        </Label>
                        <Controller
                          control={control}
                          name="city"
                          render={({ field }) => (
                            <div>
                              <Input
                                id="city"
                                inputSize={'lg'}
                                placeholder={t('checkout.form.cityPlaceholder')}
                                textSize={'sm'}
                                {...field}
                              />
                              {errors.city && (
                                <p className="mt-1 text-xs text-red-500">
                                  {errors.city.message}
                                </p>
                              )}
                            </div>
                          )}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          className="text-sm after:ml-0.5 after:text-red-500 after:content-['*']"
                          htmlFor="country"
                        >
                          {t('checkout.form.country')}
                        </Label>
                        <Controller
                          control={control}
                          name="country"
                          render={({ field }) => (
                            <div>
                              <InputSelect
                                borderColor={'border-[#979797]'}
                                className={'rounded-[10px] h-[42px] text-sm'}
                                options={countryOptions}
                                placeholder={t('checkout.form.cityPlaceholder')}
                                value={field.value}
                                onChange={(value) => {
                                  field.onChange(value);
                                  resetField('state');
                                  resetField('zip_code');
                                }}
                              />
                              {errors.country && (
                                <p className="mt-1 text-xs text-red-500">
                                  {errors.country.message}
                                </p>
                              )}
                            </div>
                          )}
                        />
                      </div>
                      <div className={country !== 'US' ? 'hidden' : 'block'}>
                        <div className="space-y-2">
                          <Label
                            className="text-sm after:ml-0.5 after:text-red-500 after:content-['*']"
                            htmlFor="state"
                          >
                            {t('checkout.form.state')}
                          </Label>
                          <Controller
                            control={control}
                            name="state"
                            render={({ field }) => (
                              <div>
                                <InputSelect
                                  borderColor={'border-[#979797]'}
                                  className={'rounded-[10px] h-[42px] text-sm'}
                                  options={stateOptions}
                                  placeholder={t(
                                    'checkout.form.statePlaceholder'
                                  )}
                                  value={field.value ?? ''}
                                  onChange={(value) => field.onChange(value)}
                                />
                                {errors.state && (
                                  <p className="mt-1 text-xs text-red-500">
                                    {errors.state.message}
                                  </p>
                                )}
                              </div>
                            )}
                          />
                        </div>
                      </div>

                      <div className={country !== 'US' ? 'hidden' : 'block'}>
                        <div className="space-y-2">
                          <Label
                            className="text-sm after:ml-0.5 after:text-red-500 after:content-['*']"
                            htmlFor="zip_code"
                          >
                            {t('checkout.form.zipCode')}
                          </Label>
                          <Controller
                            control={control}
                            name="zip_code"
                            render={({ field }) => (
                              <div>
                                <Input
                                  {...field}
                                  id="zip_code"
                                  inputSize={'lg'}
                                  placeholder={t(
                                    'checkout.form.zipCodePlaceholder'
                                  )}
                                  textSize={'sm'}
                                  value={field.value ?? ''}
                                />
                                {errors.zip_code && (
                                  <p className="mt-1 text-xs text-red-500">
                                    {errors.zip_code.message}
                                  </p>
                                )}
                              </div>
                            )}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label
                          className="text-sm after:ml-0.5 after:text-red-500 after:content-['*']"
                          htmlFor="phone_number"
                        >
                          {t('checkout.form.phoneNumber')}
                        </Label>
                        <Controller
                          control={control}
                          name="phone_number"
                          render={({ field }) => (
                            <div>
                              <Input
                                id="phone_number"
                                inputSize={'lg'}
                                placeholder={t(
                                  'checkout.form.phoneNumberPlaceholder'
                                )}
                                textSize={'sm'}
                                {...field}
                              />
                              {errors.phone_number && (
                                <p className="mt-1 text-xs text-red-500">
                                  {errors.phone_number.message}
                                </p>
                              )}
                            </div>
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className={'w-full flex items-center justify-center '}>
                  <div className=" m-auto px-6 lg:px-[60px] gap-5 flex justify-between items-center h-full">
                    {/*<Button*/}
                    {/*  className="px-8 w-[187px]"*/}
                    {/*  size="lg"*/}
                    {/*  variant="outline"*/}
                    {/*  // onClick={goBack}*/}
                    {/*>*/}
                    {/*  Save For Later*/}
                    {/*</Button>*/}
                    <Button
                      className="px-8  w-[187px]"
                      disabled={isSubmitting}
                      size="lg"
                      type="submit"
                      variant="gradient"
                    >
                      {isSubmitting ? 'Processing...' : 'Checkout'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="w-full sticky bottom-0 backdrop-blur-[30px] bg-[linear-gradient(90deg,rgba(23,29,35,0.8)_0%,rgba(23,29,35,0.8)_100%)] border-t border-[#255589] h-[100px]">
        <div className="max-w-[1440px] m-auto px-6 lg:px-[60px] flex justify-between items-center h-full">
          <Button
            className="px-8"
            size="lg"
            variant="outline"
            onClick={handlePrevious}
          >
            Previous: Add-ons
          </Button>
        </div>
      </div>
    </form>
  );
};
